import React, { useState, useEffect, useCallback } from 'react';
import { Icons } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { useTree } from '../contexts/TreeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { previewAccountDeletion, deleteAccount } from '../utils/api';
import { downloadGedcom } from '../utils/exportGedcom';
import { track } from '../utils/analytics';

/**
 * Close the account, and destroy everything it owns.
 *
 * This dialog is the only safeguard on the one irreversible action in the
 * product. Backups age out at fourteen weeks and restoring a single account is
 * not a flow that exists, so there is no undo to fall back on and no support
 * request that can put it back.
 *
 * It is therefore built to be *specific* rather than merely scary. The counts
 * are read from the server, not guessed: "all 135 people, and everything the
 * other 10 members added" is a different sentence from "your data", and the
 * difference is the entire reason this screen exists.
 *
 * It is also honest about what can be taken out first. There is no bulk export
 * in the product — the GEDCOM carries names, dates and relationships and no
 * media at all, attachments come out one at a time through the View link on
 * each row, and profile photos have no download path. Telling someone to "back
 * up your files first" would be pointing at something they cannot do, which is
 * worse than saying nothing.
 */
export default function DeleteAccountModal({ onClose }) {
  const { user, logout } = useAuth();
  const { people, activeTree, detachTree } = useTree();
  const { hasActiveBilling } = useSubscription();

  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Asking the server rather than reporting what this browser happens to have
    // loaded: the tree in front of them is not necessarily the only one they
    // own, and membership of other people's trees is invisible from here.
    previewAccountDeletion()
      .then((result) => { if (!cancelled) setPreview(result); })
      .catch((e) => { if (!cancelled) setLoadError(e); });
    return () => { cancelled = true; };
  }, []);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    setError(null);
    try {
      // Stop watching the tree before asking the server to destroy it.
      //
      // The listeners and the snapshot fetch are perfectly happy until the
      // documents underneath them disappear, at which point they fail with a
      // permissions error and a 404 and report themselves to /api/client-errors
      // — two entries per deletion, in the channel that exists for real browser
      // crashes. The first real deletion produced exactly that.
      //
      // Detaching rather than silencing them: an error reporter that is taught
      // to ignore errors is worse than the noise it removes, and there is
      // genuinely nothing here that should still be watching.
      detachTree();
      // Let React flush the re-render so the effect cleanups actually run — and
      // the listeners are really gone — before the server starts deleting.
      await new Promise((resolve) => setTimeout(resolve, 0));

      await deleteAccount();
      track('account_deleted');
      // The account is gone; the session is the only thing still referring to
      // it. Sign out rather than leaving the app holding a token for a user
      // that no longer exists.
      await logout();
    } catch (e) {
      setDeleting(false);
      setError(e);
    }
  }, [logout, detachTree]);

  // ── Billing has to stop first ──────────────────────────────────────────────
  // Deliberately no shortcut through to the billing screen. Deleting an account
  // is not a step in cancelling a subscription, and this dialog should not turn
  // into one.
  if (hasActiveBilling) {
    return (
      <Shell onClose={onClose} title="Cancel your subscription first">
        <p style={para}>
          Your subscription is still active. Cancel it from your subscription
          settings, then come back here.
        </p>
        <p style={para}>
          We do this in two steps so your billing is definitely stopped before
          anything is deleted.
        </p>
        <div style={footer}>
          <button onClick={onClose} style={primaryButton}>Close</button>
        </div>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell onClose={onClose} title="Delete your account">
        <p style={para}>
          We couldn't check what's on your account just now, so we haven't
          offered to delete anything. Please try again in a moment.
        </p>
        <div style={footer}>
          <button onClick={onClose} style={primaryButton}>Close</button>
        </div>
      </Shell>
    );
  }

  if (!preview) {
    return (
      <Shell onClose={onClose} title="Delete your account">
        <p style={para}>Checking what's on your account…</p>
      </Shell>
    );
  }

  const trees = preview.trees || [];
  const memberships = (preview.memberOfTrees || []).length;
  const confirmed = typed.trim().toUpperCase() === 'DELETE';

  return (
    <Shell onClose={deleting ? null : onClose} title="Delete your account">
      {trees.map((tree) => (
        <div key={tree.treeId} style={warningBox}>
          <p style={{ ...para, margin: '0 0 6px', fontWeight: 700, color: '#2C2418' }}>
            {tree.name} will be permanently deleted.
          </p>
          <p style={{ ...para, margin: 0 }}>
            All {tree.peopleCount} {tree.peopleCount === 1 ? 'person' : 'people'}, every
            photo and file
            {tree.memberCount > 0 && (
              <> — including everything the other {tree.memberCount}{' '}
              {tree.memberCount === 1 ? 'member has' : 'members have'} added</>
            )}.
            {tree.memberCount > 0 && ' They lose access immediately and are not told in advance.'}
          </p>
        </div>
      ))}

      {memberships > 0 && (
        <p style={para}>
          You'll also be removed from {memberships} other{' '}
          {memberships === 1 ? 'tree' : 'trees'} you were invited to. Those trees
          and everything in them stay exactly as they are.
        </p>
      )}

      <p style={{ ...para, fontWeight: 600, color: '#2C2418' }}>
        Save anything you want to keep first.
      </p>
      <p style={para}>
        The GEDCOM export holds names, dates and relationships — it does not
        include photos or files. To keep a photo or a document, open it with{' '}
        <strong>View</strong> on the person's record and save it. Profile photos
        can't be downloaded from the app.
      </p>

      {activeTree && people?.length > 0 && (
        <button
          onClick={() => downloadGedcom(people, activeTree?.name)}
          style={secondaryButton}
        >
          <Icons.Download size={14} /> Export GEDCOM
        </button>
      )}

      <p style={{ ...para, color: '#C0392B', fontWeight: 600 }}>
        This cannot be undone. We can't restore a deleted account.
      </p>

      <label style={{ ...para, display: 'block', marginBottom: 6 }}>
        Type <strong>DELETE</strong> to confirm:
      </label>
      <input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        disabled={deleting}
        autoFocus
        style={input}
        aria-label="Type DELETE to confirm"
      />

      {error && (
        <p style={{ ...para, color: '#C0392B' }}>
          {error.status === 409
            ? 'Your subscription is still active. Cancel it first, then come back.'
            : "Something went wrong and your account has not been deleted. Please try again."}
        </p>
      )}

      <div style={footer}>
        <button onClick={onClose} disabled={deleting} style={cancelButton}>
          Keep my account
        </button>
        <button
          onClick={handleDelete}
          disabled={!confirmed || deleting}
          style={{
            ...destructiveButton,
            opacity: confirmed && !deleting ? 1 : 0.5,
            cursor: confirmed && !deleting ? 'pointer' : 'default',
          }}
        >
          {deleting ? 'Deleting…' : 'Delete everything'}
        </button>
      </div>
    </Shell>
  );
}

function Shell({ title, onClose, children }) {
  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={header}>
          <h3 style={titleStyle}>{title}</h3>
          {onClose && (
            <button onClick={onClose} style={iconButton} aria-label="Close">
              <Icons.Close />
            </button>
          )}
        </div>
        <div style={body}>{children}</div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(44,36,24,0.5)', zIndex: 100,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backdropFilter: 'blur(4px)', padding: 16,
};

const modal = {
  width: 520, maxWidth: '100%', maxHeight: '88vh', background: '#FDFBF7',
  borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
  fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column',
};

const header = {
  padding: '20px 24px', borderBottom: '1px solid #E8E0D4',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#F5EDE3', flexShrink: 0,
};

const titleStyle = {
  margin: 0, fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif",
  color: '#2C2418', fontWeight: 700,
};

const body = { padding: 24, overflowY: 'auto' };

const para = { margin: '0 0 12px', fontSize: 13, color: '#6B5E4F', lineHeight: 1.6 };

const warningBox = {
  background: 'rgba(224,123,111,0.1)', borderRadius: 12, padding: '14px 16px',
  border: '1.5px solid rgba(224,123,111,0.3)', marginBottom: 14,
};

const input = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid rgba(107,94,79,0.3)', background: '#FFFFFF',
  fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: '#2C2418',
  boxSizing: 'border-box', marginBottom: 4,
};

const footer = {
  display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20,
};

const baseButton = {
  padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
};

const cancelButton = {
  ...baseButton, border: '1px solid rgba(107,94,79,0.3)',
  background: 'transparent', color: '#6B5E4F',
};

const destructiveButton = {
  ...baseButton, border: 'none', background: '#C0392B',
  color: '#FDFBF7', fontWeight: 700,
};

const primaryButton = {
  ...baseButton, border: '1px solid rgba(107,94,79,0.3)',
  background: 'transparent', color: '#6B5E4F',
};

const secondaryButton = {
  ...baseButton, border: '1px solid rgba(196,151,90,0.4)',
  background: 'rgba(196,151,90,0.12)', color: '#A07840',
  display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 14,
};

const iconButton = {
  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
  display: 'flex', color: '#8B7D6B',
};
